import { Extension, getMarkType } from '@tiptap/core';
import { normalizeNumber } from 'utils/number';
import { MIN_FONT_SIZE, MAX_FONT_SIZE } from '../../../../constants/fonts';

export type FontSizeOptions = {
  types: string[];
  isTable: boolean;
};

export enum ModifyFontSizeOperation {
  Increment = 'increment',
  Decrement = 'decrement',
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    fontSize: {
      setFontSize: (fontSize: string) => ReturnType;
      // increment or decrement all font sizes in the selection (can be of different font sizes)
      modifyFontSizeByOperation: (operation: ModifyFontSizeOperation) => ReturnType;
    };
  }
}

export const FontSize = Extension.create<FontSizeOptions>({
  name: 'fontSize',

  addOptions() {
    return {
      types: ['textStyle'],
      isTable: false,
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize.replace(/['"]+/g, ''),
            renderHTML: (attributes) => {
              if (!attributes.fontSize) {
                return {};
              }

              return {
                style: `font-size: ${attributes.fontSize}`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontSize:
        (fontSize: string) =>
        ({ chain, tr, state }) => {
          return chain()
            .command(({ commands }) => {
              commands.updateAttributes('paragraph', { fontSize: fontSize });
              return false;
            })
            .setMark('textStyle', { fontSize })
            .run();
        },

      modifyFontSizeByOperation:
        (operation: ModifyFontSizeOperation) =>
        ({ chain }) => {
          const textStyle = this.editor.getAttributes('textStyle');
          const fontSize = parseInt(textStyle.fontSize);
          const sizeDiff = operation === ModifyFontSizeOperation.Increment ? 1 : -1;
          const newFontSize = Number.isNaN(fontSize)
            ? 15
            : normalizeNumber(MIN_FONT_SIZE, MAX_FONT_SIZE, fontSize + sizeDiff);

          return chain()
            .command(({ commands }) => {
              commands.updateAttributes('paragraph', { fontSize: `${newFontSize}px` });
              return false;
            })
            .command(({ tr, state }) => {
              const { selection } = tr;
              const { ranges, empty } = selection;
              const type = getMarkType('textStyle', state.schema);

              // For empty selection, call setMark as usual - tiptap command handles all edge scenarios
              if (empty) {
                return chain()
                  .setMark('textStyle', { fontSize: `${newFontSize}px` })
                  .run();
              }

              ranges.forEach((range) => {
                const from = range.$from.pos;
                const to = range.$to.pos;

                state.doc.nodesBetween(from, to, (node, pos) => {
                  const trimmedFrom = Math.max(pos, from);
                  const trimmedTo = Math.min(pos + node.nodeSize, to);
                  const someHasMark = node.marks.find((mark) => mark.type === type);

                  // if there is already a mark of this type
                  // we know that we have to merge its attributes
                  // otherwise we add a fresh new mark
                  if (someHasMark) {
                    node.marks.forEach((mark) => {
                      if (type === mark.type) {
                        const prevFontSize = parseInt(mark.attrs.fontSize) || fontSize;
                        const newFontSize = normalizeNumber(MIN_FONT_SIZE, MAX_FONT_SIZE, prevFontSize + sizeDiff);

                        tr.addMark(
                          trimmedFrom,
                          trimmedTo,
                          type.create({
                            ...mark.attrs,
                            fontSize: `${newFontSize}px`,
                          }),
                        );
                      }
                    });
                  } else {
                    tr.addMark(trimmedFrom, trimmedTo, type.create({ fontSize: `${newFontSize}px` }));
                  }
                });
              });

              return true;
            })
            .run();
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      'Mod->': () => this.editor.chain().focus().modifyFontSizeByOperation(ModifyFontSizeOperation.Increment).run(),
      'Mod-<': () => this.editor.chain().focus().modifyFontSizeByOperation(ModifyFontSizeOperation.Decrement).run(),
    };
  },
});
